import React, { useEffect } from "react";
import { SEO } from "./SEO";

interface HomeSEOProps {
  title?: string;
  description?: string;
  seoData?: any;
}

export const HomeSEO: React.FC<HomeSEOProps> = ({
  title = "Evefant - Celebra todo",
  description = "Encuentra todo para tu fiesta y nosotros te lo llevamos. Desde decoración con globos, hasta los bocaditos. Delivery en todo Lima.",
  seoData,
}) => {
  // Add effect to log SEO data when component mounts or seoData changes
  useEffect(() => {
    console.log("HomeSEO component received seoData:", seoData);

    // Log each key-value pair for detailed inspection
    if (seoData) {
      console.log("SEO Data breakdown:");
      console.log("- metaTitle:", seoData.metaTitle);
      console.log("- metaDescription:", seoData.metaDescription);
      console.log("- metaRobots:", seoData.metaRobots);
      console.log("- canonicalURL:", seoData.canonicalURL);
      console.log("- keywords:", seoData.keywords);
      console.log("- structuredData:", seoData.structuredData);
      console.log("- metaImage:", seoData.metaImage);
      console.log("- openGraph:", seoData.openGraph);
    }
  }, [seoData]);

  // Basic website schema - provide a complete schema with fallback values
  const defaultSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Evefant",
    description:
      "Encuentra todo para tu fiesta y nosotros te lo llevamos. Delivery en todo Lima.",
    url: window.location.origin,
    potentialAction: {
      "@type": "SearchAction",
      target: `${window.location.origin}/search?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
    publisher: {
      "@type": "Organization",
      name: "Evefant",
      logo: {
        "@type": "ImageObject",
        url: `${window.location.origin}/logo.png`,
      },
    },
  };

  // Create a complete default OpenGraph object for fallbacks
  const defaultOpenGraph = {
    "og:title": title,
    "og:description": description,
    "og:image": {
      url: `${process.env.REACT_APP_IMAGE_FOLDER}/isotipo_azul_9c9ca23b51.webp`,
    },
    "og:url": window.location.origin,
    "og:type": "website",
  };

  // Use SEO data from API if available, otherwise use defaults
  const seoTitle = seoData?.metaTitle || title;

  // Make sure we're correctly using the metaDescription, and log when falling back
  const seoDescription = seoData?.metaDescription;
  if (!seoDescription) {
    console.warn(
      "Missing metaDescription in SEO data, falling back to default"
    );
  }

  const metaRobots = seoData?.metaRobots || "index, follow";
  const canonicalURL = seoData?.canonicalURL || window.location.origin;
  const metaKeywords =
    seoData?.keywords ||
    "evefant, celebraciones, fiestas, delivery, decoración, globos, bocaditos, lima";

  // Extract OpenGraph data if available
  const openGraph = seoData?.openGraph || defaultOpenGraph;

  // Log when we're using fallback OpenGraph data
  if (!seoData?.openGraph) {
    console.warn("Missing OpenGraph data in SEO response, using defaults");
  }

  // Log the OpenGraph data specifically
  console.log("OpenGraph data used:", openGraph);

  // Get image URL with fallbacks
  const imageUrl =
    openGraph?.["og:image"]?.url ||
    seoData?.metaImage?.url ||
    `${process.env.REACT_APP_IMAGE_FOLDER}/isotipo_azul_9c9ca23b51.webp`;

  console.log("Final image URL used:", imageUrl);
  console.log("Final description used:", seoDescription || description);

  return (
    <SEO
      title={seoTitle}
      description={seoDescription || description}
      type="website"
      schema={seoData?.structuredData || defaultSchema}
      openGraph={openGraph}
      seoData={seoData}
      image={imageUrl}
      author="Evefant"
      publisher="Evefant"
    >
      <meta name="robots" content={metaRobots} />
      <link rel="canonical" href={canonicalURL} />

      {/* Keywords meta tag */}
      <meta name="keywords" content={metaKeywords} />

      {/* Include metaViewport if available */}
      <meta name="viewport" content="width=device-width, initial-scale=1" />

      {/* Additional social media tags */}
      <meta property="og:site_name" content="Evefant - Celebra todo" />

      {/* Mobile app tags */}
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta
        name="apple-mobile-web-app-title"
        content="Evefant - Celebra todo"
      />

      {/* Add additional meta tag for proper indexing */}
      <meta name="referrer" content="origin" />

      {/* Preconnect to important third-party domains */}
      <link rel="preconnect" href={process.env.REACT_APP_IMAGE_FOLDER || ""} />
      <link rel="preconnect" href={process.env.REACT_APP_API_URL || ""} />
    </SEO>
  );
};

export default HomeSEO;
